// the hookup for the client, this is the code that gets executed when
// the client finally gets its bundle.js. At that point, the dom needs to be
// understood by react so it can take over from here on.
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Suspense, useInsertionEffect } from 'react';
import { buildStore } from '@shared/store/createStore';
import thunk from 'redux-thunk';
import { Provider } from 'react-redux';
import { getAccessTokenCB } from '@shared/components/FederatedModules/RestClient';
import { ThemeProvider, initializeIcons, Stylesheet } from '@fluentui/react';
import { Constants } from '@shared/utils/constants';
import combineReducers from '@shared/reducers/reducers';
import { RootHandler } from '@shared/handlers/rootHandler';
import { getWindow } from '@shared/services/window';
import { decodeVulnerableDataInInitalState, generateGuid, logInitialTelemetryEvents, setProfileFeatureFlag } from '@shared/utils/appUtils';
import { createEnableNewNpsAction, createRehydrateClientStateAction, createChangeLocaleLanguage, createSetInitializedPCAInstanceAction } from '@shared/actions/actions';
import { initBrowserConfig } from '@shared/services/init/initBrowser';
import { changeBillingRegion, getTenantType, getUserTenantsDetails, setStorefrontName, updateUser } from '@shared/actions/thunkActions';
import { HttpModule } from '@shared/services/http/httpProtocol';
import { ensureClientLoggerInitialization, logger } from '@src/logger';
import { getCookieItem, saveCookieItem } from '@shared/utils/browserStorageUtils';
import ScrollToTopButton from '@shared/components/scrollToTopButton';
import Router from './Router';
import { refreshTokenIfNeeded } from './refreshTokenHandler';
import i18next from 'i18next';
import i18nInit from '@shared/i18n/client';
import { I18nextProvider } from 'react-i18next';
import { onError } from '@shared/handlers/onError';
import { appendHeaderTags } from '@shared/handlers/seo';
import RemoveEnvs from '@agoras/envs';
import RemoteLogger from '@agoras/logger';
import RestClient, { queryClient } from '@agoras/rest-client';
import AuthClient from '@shared/msal/client';
import { createGuestUser } from '@shared/msal/user';
import { postRedirect } from '@shared/msal/postRedirect';
import { Hydrate, QueryClientProvider } from '@tanstack/react-query';
const CHECK_TOKEN_INTERVAL_MSEC = 1000 * 60 * 10; // 10 minutes in milliseconds
const refreshTokenIfNeededInterval = (store)=>_async_to_generator(function*() {
        var _getWindow;
        yield refreshTokenIfNeeded(store);
        (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.setTimeout(refreshTokenIfNeededInterval, CHECK_TOKEN_INTERVAL_MSEC, store);
    })();
export function initApp() {
    var _store_getState_users;
    const currentWindow = getWindow();
    const initialState = currentWindow.__INITIAL_STATE__;
    decodeVulnerableDataInInitalState(initialState);
    const prevCorrelationId = getCookieItem(Constants.Cookies.correlationId, false);
    if (!prevCorrelationId) {
        const correlationId = generateGuid();
        initialState.config.correlationId = correlationId;
        currentWindow.__INITIAL_STATE__ = initialState;
        saveCookieItem(Constants.Cookies.correlationId, correlationId, 'expires=0');
    }
    const store = buildStore({
        combinedReducers: combineReducers,
        initialState,
        middleware: [
            thunk
        ]
    });
    const localeFromUrl = currentWindow.location.pathname.split('/')[1];
    saveCookieItem(Constants.Cookies.LocaleCookie, localeFromUrl);
    initBrowserConfig(Constants.BrowserConfigs.appsource, store);
    initializeIcons();
    i18nInit();
    HttpModule.updateAccessToken((_store_getState_users = store.getState().users) === null || _store_getState_users === void 0 ? void 0 : _store_getState_users.accessToken);
    HttpModule.updateStore(store);
    logInitialTelemetryEvents(store);
    setProfileFeatureFlag(store);
    currentWindow === null || currentWindow === void 0 ? void 0 : currentWindow.setTimeout(refreshTokenIfNeededInterval, CHECK_TOKEN_INTERVAL_MSEC, store);
    const stylesheet = Stylesheet.getInstance();
    stylesheet.setConfig({
        namespace: Constants.AppName.AppSource
    });
    store.dispatch(setStorefrontName(Constants.StorefrontName.AppSource));
    store.dispatch(getUserTenantsDetails());
    store.dispatch(getTenantType());
    store.dispatch(changeBillingRegion({
        shouldSetCountyCodeCookie: false
    }));
    store.dispatch(createRehydrateClientStateAction());
    store.dispatch(createEnableNewNpsAction({
        enable: true
    }));
    store.dispatch(createChangeLocaleLanguage({
        locale: localeFromUrl
    }));
    onError(currentWindow, store);
    ensureClientLoggerInitialization(store);
    RemoveEnvs.init(store.getState().config.agorasEnvironment);
    RemoteLogger.init(logger.clientLogger.logger, {
        correlationId: initialState.config.correlationId,
        hostType: Constants.StorefrontName.AppSource
    });
    RestClient.init(()=>getAccessTokenCB(store));
    AuthClient.initialize({
        onSuccess: ()=>{
            store.dispatch(createSetInitializedPCAInstanceAction({
                initialized: true
            }));
            postRedirect();
        },
        onFailure: ()=>store.dispatch(updateUser({
                userDetails: createGuestUser()
            }))
    });
    return store;
}
const dehydratedState = window.__REACT_QUERY_STATE__;
const App = ({ store })=>{
    useInsertionEffect(()=>{
        appendHeaderTags(store.getState());
    }, []);
    return /*#__PURE__*/ _jsx(RootHandler, {
        landingView: store.getState().config.landingView,
        store: store,
        children: /*#__PURE__*/ _jsx(ThemeProvider, {
            children: /*#__PURE__*/ _jsx(QueryClientProvider, {
                client: queryClient,
                children: /*#__PURE__*/ _jsxs(Hydrate, {
                    state: dehydratedState,
                    children: [
                        /*#__PURE__*/ _jsx(I18nextProvider, {
                            i18n: i18next,
                            children: /*#__PURE__*/ _jsx(Provider, {
                                store: store,
                                children: /*#__PURE__*/ _jsx(Suspense, {
                                    fallback: null,
                                    children: /*#__PURE__*/ _jsx(Router, {})
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(ScrollToTopButton, {})
                    ]
                })
            })
        })
    });
};
export default App;
